
import React, { useState, useEffect, useMemo } from 'react';
import type { Project, User, Country as CountryType, Vessel } from '../types';
import { ProjectStatus, Country } from '../types';
import { TANZANIA_REGIONS, UGANDA_DISTRICTS, KENYA_COUNTIES } from '../constants';
import { apiService } from '../services/apiService';
import { ProjectTable } from '../components/ProjectTable';
import { ProjectCard } from '../components/ProjectCard';
import { ProjectDetailsModal } from '../components/ProjectDetailsModal';
import { StatsOverview } from '../components/StatsOverview';
import { ProjectModal } from '../components/ProjectModal';
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import VesselList from '../components/PortBitumen/VesselList';
import { 
  Plus, Search, RefreshCw, LayoutGrid, List, MapPin, 
  AlertTriangle, Loader2, Globe, Filter, Database, Ship, 
  Route, Anchor
} from 'lucide-react';

interface ProjectsModuleProps {
  currentUser: User;
  activeCountry: CountryType;
}

const ProjectsModule: React.FC<ProjectsModuleProps> = ({ currentUser, activeCountry }) => {
  const [view, setView] = useState<'PROJECTS' | 'VESSELS'>('PROJECTS');
  const [projects, setProjects] = useState<Project[]>([]);
  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState<string>('All');
  const [selectedStatus, setSelectedStatus] = useState<string>('All');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [viewingProject, setViewingProject] = useState<Project | null>(null);
  const [displayMode, setDisplayMode] = useState<'table' | 'grid'>('table');
  const [isGlobalView, setIsGlobalView] = useState(false);
  
  // Deletion State
  const [projectToDelete, setProjectToDelete] = useState<string | null>(null);
  const [vesselToDelete, setVesselToDelete] = useState<string | null>(null);

  const isAdmin = currentUser.role === 'Admin';
  const canAddEdit = isAdmin || currentUser.role === 'Add/Edit';
  const canDelete = isAdmin;
  const isReadOnly = currentUser.role === 'Read Only';

  useEffect(() => {
    if (view === 'PROJECTS') {
      loadProjects();
    } else {
      loadVessels();
    }
  }, [activeCountry, isGlobalView, view]);

  const loadProjects = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiService.getProjects();
      setProjects(isGlobalView ? data : data.filter(p => p.country === activeCountry));
    } catch (err: any) {
      setError("Unable to access project registry.");
      setProjects([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadVessels = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiService.getVessels();
      setVessels(data);
    } catch (err: any) {
      setError("Unable to access vessel registry.");
      setVessels([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getRegionsForCountry = () => {
    if (isGlobalView) return [];
    switch(activeCountry) {
        case Country.TANZANIA: return TANZANIA_REGIONS;
        case Country.UGANDA: return UGANDA_DISTRICTS;
        case Country.KENYA: return KENYA_COUNTIES;
        default: return [];
    }
  };

  const filteredProjects = useMemo(() => {
    return projects.filter(p => {
      const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.region.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.contractor.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRegion = isGlobalView || selectedRegion === 'All' || p.region === selectedRegion;
      const matchesStatus = selectedStatus === 'All' || p.status === selectedStatus;
      return matchesSearch && matchesRegion && matchesStatus;
    });
  }, [projects, searchTerm, selectedRegion, selectedStatus, isGlobalView]);

  const handleSubmit = async (project: Project) => {
    if (isReadOnly) return;
    const projectWithCountry = { ...project, country: project.country || activeCountry };
    const success = await apiService.saveProject(projectWithCountry);
    if (success) {
      loadProjects();
      setIsModalOpen(false);
    }
  };

  const handleUpdateVessel = async (updatedVessel: Vessel) => {
    if (isReadOnly) return;
    await apiService.saveVessel(updatedVessel);
    setVessels(prev => prev.map(v => v.id === updatedVessel.id ? updatedVessel : v));
  };

  const handleConfirmDeleteProject = async () => {
    if (!projectToDelete || !canDelete) return;
    try {
      const success = await apiService.deleteProject(projectToDelete);
      if (success) setProjects(prev => prev.filter(p => p.id !== projectToDelete));
    } finally {
      setProjectToDelete(null);
    }
  };

  const handleConfirmDeleteVessel = async () => {
    if (!vesselToDelete || !canDelete) return;
    try {
      const success = await apiService.deleteVessel(vesselToDelete);
      if (success) setVessels(prev => prev.filter(v => v.id !== vesselToDelete));
    } finally {
      setVesselToDelete(null);
    }
  };

  return (
    <div className="p-8 space-y-8 animate-in fade-in duration-500">
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <h2 className="text-3xl font-black text-slate-800 tracking-tight">
                {view === 'PROJECTS' ? (isGlobalView ? 'Global Portfolio' : `${activeCountry} Pipeline`) : 'Maritime Registry'}
            </h2>
            {isGlobalView && view === 'PROJECTS' && (
                <div className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-full text-[10px] font-black uppercase tracking-widest border border-indigo-100 flex items-center gap-2">
                    <Globe size={12} /> Master Registry
                </div>
            )}
          </div>
          <div className="flex items-center gap-6 border-b border-slate-200">
              <button 
                onClick={() => setView('PROJECTS')}
                className={`pb-4 text-[11px] font-black uppercase tracking-widest flex items-center gap-2 border-b-2 transition-all ${view === 'PROJECTS' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
              >
                  <Route size={16} /> Infrastructure Projects
              </button>
              <button 
                onClick={() => setView('VESSELS')}
                className={`pb-4 text-[11px] font-black uppercase tracking-widest flex items-center gap-2 border-b-2 transition-all ${view === 'VESSELS' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
              >
                  <Ship size={16} /> Vessel Registry
              </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          {view === 'PROJECTS' && isAdmin && (
            <div className="bg-slate-100 p-1 rounded-2xl flex border border-slate-200 shadow-inner">
                <button 
                    onClick={() => setIsGlobalView(false)}
                    className={`px-5 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${!isGlobalView ? 'bg-white text-indigo-600 shadow-md' : 'text-slate-400 hover:text-slate-600'}`}
                >
                    Branch View
                </button>
                <button 
                    onClick={() => setIsGlobalView(true)}
                    className={`px-5 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${isGlobalView ? 'bg-white text-indigo-600 shadow-md' : 'text-slate-400 hover:text-slate-600'}`}
                >
                    Global View
                </button>
            </div>
          )}
          {view === 'PROJECTS' && canAddEdit && (
            <button 
              onClick={() => { setEditingProject(null); setIsModalOpen(true); }}
              className="bg-indigo-600 text-white px-6 py-2.5 rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg shadow-indigo-200 hover:bg-indigo-700 transition-all flex items-center"
            >
              <Plus size={18} className="mr-2" /> New Project
            </button>
          )}
        </div>
      </header>

      {error && (
          <div className="bg-rose-50 border border-rose-100 p-4 rounded-2xl flex items-center gap-3 text-rose-600 font-bold text-sm">
             <AlertTriangle size={20} />
             {error}
             <button onClick={view === 'PROJECTS' ? loadProjects : loadVessels} className="ml-auto flex items-center gap-1 hover:underline text-xs">
                <RefreshCw size={12}/> Retry
             </button>
          </div>
      )}

      {view === 'PROJECTS' ? (
          <>
            {!isGlobalView && <StatsOverview projects={projects} />}

            <div className="bg-white p-4 rounded-3xl border border-slate-200 shadow-sm flex flex-col md:flex-row gap-4 items-center justify-between">
                <div className="flex flex-col md:flex-row gap-4 flex-1 w-full">
                    <div className="relative flex-1">
                        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Search projects..."
                            className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all text-sm font-medium"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {!isGlobalView && (
                        <div className="relative min-w-[200px]">
                            <MapPin size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                            <select
                                value={selectedRegion}
                                onChange={(e) => setSelectedRegion(e.target.value)}
                                className="w-full pl-11 pr-10 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all text-sm font-black text-slate-700 appearance-none cursor-pointer"
                            >
                                <option value="All">All Regions</option>
                                {getRegionsForCountry().map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                            <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                                <Filter size={14} />
                            </div>
                        </div>
                    )}
                </div>
                
                <div className="flex items-center gap-4 px-2">
                    <button onClick={loadProjects} className="p-2.5 text-slate-400 hover:text-indigo-600 bg-slate-50 rounded-xl border border-slate-100 transition-all" title="Synchronize Projects">
                        <RefreshCw size={20} className={isLoading ? 'animate-spin' : ''} />
                    </button>
                    <div className="flex bg-slate-100 p-1 rounded-2xl border border-slate-200">
                        <button onClick={() => setDisplayMode('grid')} className={`p-2 rounded-xl transition-all ${displayMode === 'grid' ? 'bg-white shadow-md text-indigo-600' : 'text-slate-400 hover:text-slate-600'}`}><LayoutGrid size={20} /></button>
                        <button onClick={() => setDisplayMode('table')} className={`p-2 rounded-xl transition-all ${displayMode === 'table' ? 'bg-white shadow-md text-indigo-600' : 'text-slate-400 hover:text-slate-600'}`}><List size={20} /></button>
                    </div>
                </div>
            </div>

            {isLoading ? (
                <div className="py-32 flex flex-col items-center justify-center gap-6">
                    <Loader2 className="animate-spin text-indigo-500" size={48} />
                    <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">Synchronizing Master Ledger...</p>
                </div>
            ) : displayMode === 'table' ? (
                <ProjectTable 
                    projects={filteredProjects} 
                    onEdit={(p) => { setEditingProject(p); setIsModalOpen(true); }} 
                    onDelete={(id) => setProjectToDelete(id)} 
                    onView={(p) => { setViewingProject(p); setIsDetailsOpen(true); }} 
                    canEdit={canAddEdit}
                    canDelete={canDelete}
                    isGlobalMode={isGlobalView}
                />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {filteredProjects.map(project => (
                        <ProjectCard 
                            key={project.id} 
                            project={project} 
                            onEdit={(p) => { setEditingProject(p); setIsModalOpen(true); }} 
                            onDelete={(id) => setProjectToDelete(id)} 
                            onView={(p) => { setViewingProject(p); setIsDetailsOpen(true); }} 
                            canEdit={canAddEdit}
                            canDelete={canDelete}
                            showCountry={isGlobalView}
                        />
                    ))}
                </div>
            )}
          </>
      ) : (
          <div className="animate-in slide-in-from-right-4 duration-500">
              <VesselList 
                vessels={vessels} 
                onDelete={(id) => setVesselToDelete(id)} 
                onUpdate={handleUpdateVessel} 
                currentUser={currentUser}
              />
          </div>
      )}

      {isModalOpen && (
        <ProjectModal 
          isOpen={isModalOpen} 
          onClose={() => setIsModalOpen(false)} 
          onSubmit={handleSubmit} 
          editingProject={editingProject} 
          activeCountry={activeCountry} 
        />
      )}
      
      {isDetailsOpen && (
        <ProjectDetailsModal 
          isOpen={isDetailsOpen} 
          onClose={() => setIsDetailsOpen(false)} 
          project={viewingProject} 
          onUpdate={handleSubmit} 
        />
      )}

      <ConfirmationDialog 
        isOpen={!!projectToDelete}
        title="Revoke Infrastructure Record"
        message="Are you sure you want to permanently remove this project from the national registry? This action is audited and cannot be undone."
        confirmText="Delete Project"
        cancelText="Discard"
        isDestructive={true}
        onConfirm={handleConfirmDeleteProject}
        onCancel={() => setProjectToDelete(null)}
      />

      <ConfirmationDialog 
        isOpen={!!vesselToDelete}
        title="Revoke Vessel Registry"
        message="Are you sure you want to permanently remove this vessel and all its manifest records? This action is audited and cannot be undone."
        confirmText="Delete Vessel"
        cancelText="Discard"
        isDestructive={true}
        onConfirm={handleConfirmDeleteVessel}
        onCancel={() => setVesselToDelete(null)}
      />
    </div>
  );
};

export default ProjectsModule;
